import React, { forwardRef, useImperativeHandle, useState, useCallback, useRef } from 'react';
import { Platform, ScrollView, StyleSheet, Image, Pressable } from 'react-native';
import { H2, H5, YStack, XStack, Button as TamaguiButton, Text as TamaguiText } from 'tamagui';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Dialog } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons, AntDesign } from '@expo/vector-icons';
import { View, Text } from '@constants/Themed';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import apis from '@api/api';
import { useQueryClient } from '@tanstack/react-query';
import * as Animatable from 'react-native-animatable';
import AnimatedLottieView from '@utils/lottieAnimation';

interface AgentBuilderModalProps {
  userList?: any[];
  onAgentCreated?: () => void;
}

export type AgentBuilderModalHandle = {
  openModal: () => void;
  closeModal: () => void;
}

const steps = ['Select Agent Type', 'Configure Agent', 'Review & Finish'];

const agentTypes = [
  {
    type: 'assistant',
    name: 'Assistant',
    description: 'An assistant agent that can help with tasks and answer questions',
    image: require('@assets/images/1x/Assistant.png')
  },
  {
    type: 'business_developer',
    name: 'Business Developer',
    description: 'Focuses on business development, partnerships, and growth strategies',
    image: require('@assets/images/1x/BusinessDeveloper.png')
  },
  {
    type: 'sales_marketer',
    name: 'Sales Marketer',
    description: 'Handles sales activities, marketing campaigns, and customer engagement',
    image: require('@assets/images/1x/SalesMarketer.png')
  },
  {
    type: 'researcher',
    name: 'Researcher',
    description: 'Conducts research, data analysis, and provides insights',
    image: require('@assets/images/1x/Researcher.png')
  }
];

const AgentBuilderModal = forwardRef<AgentBuilderModalHandle, AgentBuilderModalProps>((props, ref) => {
  const { userList = [], onAgentCreated } = props;
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedAgentType, setSelectedAgentType] = useState<string | null>(null);
  const [agentMode, setAgentMode] = useState<'standalone' | 'doppelganger'>('standalone');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [agentName, setAgentName] = useState('');
  const [agentDescription, setAgentDescription] = useState('');
  const [triggers, setTriggers] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzingContext, setAnalyzingContext] = useState(false);
  const formRef = useRef(null);

  // Custom Stepper Component using Tamagui
  const CustomStepper = ({ activeStep, steps }: { activeStep: number; steps: string[] }) => {
    return (
      <View style={{
        paddingHorizontal: 20,
        paddingVertical: 16,
        backgroundColor: theme ? '#ffffff' : '#1e293b',
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e2e8f0' : '#334155'
      }}>
        <XStack alignItems="center" justifyContent="center" gap={8}>
          {steps.map((label, index) => (
            <React.Fragment key={index}>
              <YStack alignItems="center" flex={1} gap={8}>
                <View style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: index <= activeStep
                    ? (index === activeStep ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#10b981' : '#34d399'))
                    : (theme ? '#e2e8f0' : '#334155'),
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 2,
                  borderColor: index <= activeStep
                    ? (index === activeStep ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#10b981' : '#34d399'))
                    : (theme ? '#e2e8f0' : '#334155')
                }}>
                  {index < activeStep ? (
                    <MaterialIcons
                      name="check"
                      size={18}
                      color="white"
                    />
                  ) : (
                    <TamaguiText style={{
                      color: index === activeStep ? 'white' : (theme ? '#64748b' : '#94a3b8'),
                      fontSize: 14,
                      fontWeight: '600'
                    }}>
                      {index + 1}
                    </TamaguiText>
                  )}
                </View>
                <TamaguiText style={{
                  fontSize: 11,
                  textAlign: 'center',
                  color: index === activeStep
                    ? (theme ? '#1e293b' : '#f1f5f9')
                    : (index < activeStep ? (theme ? '#10b981' : '#34d399') : (theme ? '#64748b' : '#94a3b8')),
                  fontWeight: index === activeStep ? '700' : '400',
                  maxWidth: 80,
                }}
                  numberOfLines={2}
                >
                  {label}
                </TamaguiText>
              </YStack>
              {index < steps.length - 1 && (
                <View style={{
                  flex: 1,
                  height: 2,
                  backgroundColor: index < activeStep
                    ? (theme ? '#10b981' : '#34d399')
                    : (theme ? '#e2e8f0' : '#334155'),
                  marginHorizontal: 4,
                  marginTop: -20,
                }} />
              )}
            </React.Fragment>
          ))}
        </XStack>
      </View>
    );
  };

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setVisible(true);
      setActiveStep(0);
      resetForm();
    },
    closeModal: () => {
      setVisible(false);
      resetForm();
    }
  }));

  const resetForm = () => {
    setSelectedAgentType(null);
    setAgentMode('standalone');
    setSelectedUserId(null);
    setAgentName('');
    setAgentDescription('');
    setTriggers([]);
    setActions([]);
    setActiveStep(0);
  };

  const handleNext = () => {
    console.log("activeStep", activeStep);
    if (activeStep === 0 && !selectedAgentType) {
      return; // Can't proceed without selecting agent type
    }
    if (activeStep === 1) {
      // Get form values directly (state updates are async)
      const formValues = formRef.current?.values || {};
      console.log("Form values:", JSON.stringify(formValues, null, 2));
      console.log("Form ref current:", formRef.current);
      console.log("Current agentName state:", agentName);
      console.log("Current agentMode:", agentMode);
      console.log("Current selectedUserId:", selectedUserId);

      // Try multiple possible field name variations
      // GenerateSchemaForm might use title (lowercased), ref, or index-based keys
      const formName = formValues.name || formValues.Name || formValues['0'] ||
        (Object.values(formValues).find((v: any) => typeof v === 'string' && v.trim()) as string) || '';
      const formDescription = formValues.description || formValues.Description || formValues['1'] || '';
      const formUser = formValues.user || formValues.user_stores;

      console.log("Extracted values:", { formName, formDescription, formUser });

      // Update state for later use
      if (formName && typeof formName === 'string') setAgentName(formName);
      if (formDescription && typeof formDescription === 'string') setAgentDescription(formDescription);
      if (formUser) {
        const userId = formUser._id || formUser.value || formUser;
        setSelectedUserId(userId);
      }

      // Validate doppelganger mode requires user
      if (agentMode === 'doppelganger' && !selectedUserId && !formUser) {
        console.log("Validation failed: doppelganger mode requires user");
        return; // Can't proceed without selecting user for doppelganger
      }

      // Validate agent name (check both state and form values)
      const nameToCheck = (typeof formName === 'string' ? formName : '') || agentName || '';
      console.log("Name to check:", nameToCheck, "trimmed length:", nameToCheck.trim().length);
      if (!nameToCheck.trim()) {
        console.log("Validation failed: agent name is required");
        return; // Can't proceed without agent name
      }

      console.log("All validations passed, proceeding to next step");
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAnalyzeContext = async () => {
    if (!selectedUserId) return;
    setAnalyzingContext(true);
    try {
      // Context analysis will happen on backend when creating agent
      // This is just a preview/confirmation
      await new Promise(resolve => setTimeout(resolve, 1500));
    } catch (error) {
      console.error('Error analyzing context:', error);
    } finally {
      setAnalyzingContext(false);
    }
  };

  const handleCreateAgent = async () => {
    // Get final values from formik if available
    const finalName = formRef.current?.values?.name || agentName;
    const finalDescription = formRef.current?.values?.description || agentDescription;
    const finalUserId = agentMode === 'doppelganger'
      ? (formRef.current?.values?.user?._id || formRef.current?.values?.user?.value || formRef.current?.values?.user || selectedUserId)
      : undefined;

    if (!selectedAgentType || !finalName?.trim()) {
      return;
    }

    setLoading(true);
    try {
      const agentData = {
        name: finalName.trim(),
        description: finalDescription?.trim() || '',
        type: selectedAgentType,
        mode: agentMode,
        userId: finalUserId,
        triggers: triggers.map(t => ({
          type: t,
          conditions: {}
        })),
        actions: actions.map(a => ({
          type: a,
          config: {}
        }))
      };

      const result = await apis.post('/agents/create', agentData);

      if (result.data.success) {
        // Invalidate queries to refresh hierarchy
        queryClient.invalidateQueries({ queryKey: ['userHierarchy'] });

        if (onAgentCreated) {
          onAgentCreated();
        }

        setVisible(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingHorizontal: 24,
              paddingVertical: 20,
              gap: 16,
              flexGrow: 1
            }}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <YStack gap={16}>
              <TamaguiText style={{
                color: theme ? '#64748b' : '#94a3b8',
                fontSize: 16,
                textAlign: 'center',
                marginBottom: 8
              }}>
                Select the type of agent you want to create
              </TamaguiText>

              {agentTypes.map((agent) => (
                <Pressable
                  key={agent.type}
                  onPress={() => setSelectedAgentType(agent.type)}
                >
                  <Animatable.View
                    animation="fadeInUp"
                    style={[
                      styles.agentCard,
                      {
                        backgroundColor: theme ? '#ffffff' : '#1e293b',
                        borderColor: selectedAgentType === agent.type
                          ? (theme ? '#3b82f6' : '#2563eb')
                          : (theme ? '#e2e8f0' : '#334155'),
                        borderWidth: selectedAgentType === agent.type ? 3 : 2,
                      }
                    ]}
                  >
                    <XStack gap={16} alignItems="center">
                      <View style={{
                        width: 80,
                        height: 80,
                        borderRadius: 12,
                        overflow: 'hidden',
                        backgroundColor: theme ? '#f1f5f9' : '#334155'
                      }}>
                        <Image
                          source={agent.image}
                          style={{ width: '100%', height: '100%' }}
                          resizeMode="cover"
                        />
                      </View>
                      <YStack flex={1} gap={4}>
                        <TamaguiText style={{
                          color: theme ? '#1e293b' : '#f1f5f9',
                          fontSize: 18,
                          fontWeight: '700'
                        }}>
                          {agent.name}
                        </TamaguiText>
                        <TamaguiText style={{
                          color: theme ? '#64748b' : '#94a3b8',
                          fontSize: 14
                        }}>
                          {agent.description}
                        </TamaguiText>
                      </YStack>
                      {selectedAgentType === agent.type && (
                        <MaterialIcons
                          name="check-circle"
                          size={24}
                          color={theme ? '#3b82f6' : '#2563eb'}
                        />
                      )}
                    </XStack>
                  </Animatable.View>
                </Pressable>
              ))}
            </YStack>
          </ScrollView>
        );
      case 1:
        return (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingHorizontal: 24,
              paddingVertical: 20,
              gap: 24,
              flexGrow: 1
            }}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <YStack gap={24}>
              {/* Agent Mode Selection */}
              <YStack gap={12}>
                <TamaguiText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 16,
                  fontWeight: '600'
                }}>
                  Agent Mode
                </TamaguiText>
                <YStack gap={8}>
                  <Pressable onPress={() => setAgentMode('standalone')}>
                    <View style={[
                      styles.radioOption,
                      {
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        borderColor: agentMode === 'standalone'
                          ? (theme ? '#3b82f6' : '#2563eb')
                          : (theme ? '#e2e8f0' : '#334155'),
                      }
                    ]}>
                      <XStack gap={12} alignItems="center">
                        <MaterialIcons
                          name={agentMode === 'standalone' ? 'radio-button-checked' : 'radio-button-unchecked'}
                          size={24}
                          color={agentMode === 'standalone' ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#64748b' : '#94a3b8')}
                        />
                        <YStack flex={1}>
                          <TamaguiText style={{
                            color: theme ? '#1e293b' : '#f1f5f9',
                            fontSize: 14,
                            fontWeight: '600'
                          }}>
                            Standalone (Self-automated)
                          </TamaguiText>
                          <TamaguiText style={{
                            color: theme ? '#64748b' : '#94a3b8',
                            fontSize: 12
                          }}>
                            Agent operates independently with predefined rules
                          </TamaguiText>
                        </YStack>
                      </XStack>
                    </View>
                  </Pressable>
                  <Pressable onPress={() => setAgentMode('doppelganger')}>
                    <View style={[
                      styles.radioOption,
                      {
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        borderColor: agentMode === 'doppelganger'
                          ? (theme ? '#3b82f6' : '#2563eb')
                          : (theme ? '#e2e8f0' : '#334155'),
                      }
                    ]}>
                      <XStack gap={12} alignItems="center">
                        <MaterialIcons
                          name={agentMode === 'doppelganger' ? 'radio-button-checked' : 'radio-button-unchecked'}
                          size={24}
                          color={agentMode === 'doppelganger' ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#64748b' : '#94a3b8')}
                        />
                        <YStack flex={1}>
                          <TamaguiText style={{
                            color: theme ? '#1e293b' : '#f1f5f9',
                            fontSize: 14,
                            fontWeight: '600'
                          }}>
                            Doppelganger (Based on user context)
                          </TamaguiText>
                          <TamaguiText style={{
                            color: theme ? '#64748b' : '#94a3b8',
                            fontSize: 12
                          }}>
                            Agent learns from and mimics a specific user's behavior
                          </TamaguiText>
                        </YStack>
                      </XStack>
                    </View>
                  </Pressable>
                </YStack>
              </YStack>

              {/* Doppelganger User Selection */}
              {agentMode === 'doppelganger' && (
                <YStack gap={12}>
                  <TamaguiText style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontSize: 16,
                    fontWeight: '600'
                  }}>
                    Select User for Context Analysis
                  </TamaguiText>
                  <GenerateSchemaForm
                    ref={formRef}
                    schema={[
                      {
                        type: 'string',
                        title: 'User',
                        ref: 'user_select',
                        required: true
                      }
                    ]}
                    hideButton
                  />
                  {formRef.current?.values?.user && (
                    <TamaguiText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 12,
                      marginTop: 8
                    }}>
                      Selected: {formRef.current.values.user?.profile_information?.name || formRef.current.values.user?.email || 'User'}
                    </TamaguiText>
                  )}
                  {selectedUserId && (
                    <TamaguiButton
                      onPress={handleAnalyzeContext}
                      size="$4"
                      style={{
                        backgroundColor: theme ? '#7c3aed' : '#6d28d9',
                      }}
                      disabled={analyzingContext}
                    >
                      {analyzingContext ? (
                        <TamaguiText>Analyzing...</TamaguiText>
                      ) : (
                        <TamaguiText>Analyze User Context</TamaguiText>
                      )}
                    </TamaguiButton>
                  )}
                </YStack>
              )}

              {/* Agent Configuration */}
              <YStack gap={12}>
                <TamaguiText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 16,
                  fontWeight: '600'
                }}>
                  Agent Configuration
                </TamaguiText>
                <GenerateSchemaForm
                  ref={formRef}
                  schema={[
                    { type: 'string', title: 'Name', required: true },
                    { type: 'string', title: 'Description' },
                  ]}
                  hideButton
                />
              </YStack>

              {/* Triggers */}
              <YStack gap={12}>
                <TamaguiText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 16,
                  fontWeight: '600'
                }}>
                  Triggers (What should activate this agent?)
                </TamaguiText>
                {['data_change', 'project_change', 'user_action', 'message_received'].map((trigger) => (
                  <Pressable
                    key={trigger}
                    onPress={() => {
                      setTriggers(prev =>
                        prev.includes(trigger)
                          ? prev.filter(t => t !== trigger)
                          : [...prev, trigger]
                      );
                    }}
                  >
                    <View style={[
                      styles.checkboxItem,
                      {
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        borderColor: triggers.includes(trigger)
                          ? (theme ? '#3b82f6' : '#2563eb')
                          : (theme ? '#e2e8f0' : '#334155'),
                      }
                    ]}>
                      <XStack gap={12} alignItems="center">
                        <MaterialIcons
                          name={triggers.includes(trigger) ? 'check-box' : 'check-box-outline-blank'}
                          size={24}
                          color={triggers.includes(trigger) ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#64748b' : '#94a3b8')}
                        />
                        <TamaguiText style={{
                          color: theme ? '#1e293b' : '#f1f5f9',
                          fontSize: 14
                        }}>
                          {trigger.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </TamaguiText>
                      </XStack>
                    </View>
                  </Pressable>
                ))}
              </YStack>

              {/* Actions */}
              <YStack gap={12}>
                <TamaguiText style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 16,
                  fontWeight: '600'
                }}>
                  Actions (What should the agent do?)
                </TamaguiText>
                {['notify', 'create_task', 'update_data', 'send_message'].map((action) => (
                  <Pressable
                    key={action}
                    onPress={() => {
                      setActions(prev =>
                        prev.includes(action)
                          ? prev.filter(a => a !== action)
                          : [...prev, action]
                      );
                    }}
                  >
                    <View style={[
                      styles.checkboxItem,
                      {
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        borderColor: actions.includes(action)
                          ? (theme ? '#3b82f6' : '#2563eb')
                          : (theme ? '#e2e8f0' : '#334155'),
                      }
                    ]}>
                      <XStack gap={12} alignItems="center">
                        <MaterialIcons
                          name={actions.includes(action) ? 'check-box' : 'check-box-outline-blank'}
                          size={24}
                          color={actions.includes(action) ? (theme ? '#3b82f6' : '#2563eb') : (theme ? '#64748b' : '#94a3b8')}
                        />
                        <TamaguiText style={{
                          color: theme ? '#1e293b' : '#f1f5f9',
                          fontSize: 14
                        }}>
                          {action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </TamaguiText>
                      </XStack>
                    </View>
                  </Pressable>
                ))}
              </YStack>
            </YStack>
          </ScrollView>
        );
      case 2:
        const selectedAgent = agentTypes.find(a => a.type === selectedAgentType);
        return (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingHorizontal: 24,
              paddingVertical: 20,
              gap: 24,
              flexGrow: 1
            }}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <YStack gap={24}>
              <H2 style={{
                color: theme ? '#1e293b' : '#f1f5f9',
                textAlign: 'center'
              }}>
                Review Your Agent
              </H2>

              {/* Agent Summary */}
              <View style={[
                styles.summaryCard,
                { backgroundColor: theme ? '#f8fafc' : '#1e293b' }
              ]}>
                <YStack gap={12}>
                  <XStack gap={12} alignItems="center">
                    {selectedAgent && (
                      <Image
                        source={selectedAgent.image}
                        style={{ width: 60, height: 60, borderRadius: 8 }}
                        resizeMode="cover"
                      />
                    )}
                    <YStack flex={1}>
                      <TamaguiText style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: 18,
                        fontWeight: '700'
                      }}>
                        {agentName || 'Unnamed Agent'}
                      </TamaguiText>
                      <TamaguiText style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 14
                      }}>
                        {selectedAgent?.name}
                      </TamaguiText>
                    </YStack>
                  </XStack>

                  {agentDescription && (
                    <TamaguiText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {agentDescription}
                    </TamaguiText>
                  )}

                  <XStack gap={8} alignItems="center">
                    <MaterialIcons
                      name={agentMode === 'standalone' ? 'smart-toy' : 'person'}
                      size={20}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                    <TamaguiText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      Mode: {agentMode === 'standalone' ? 'Standalone' : 'Doppelganger'}
                    </TamaguiText>
                  </XStack>

                  {triggers.length > 0 && (
                    <YStack gap={8}>
                      <TamaguiText style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: 14,
                        fontWeight: '600'
                      }}>
                        Triggers: {triggers.length}
                      </TamaguiText>
                    </YStack>
                  )}

                  {actions.length > 0 && (
                    <YStack gap={8}>
                      <TamaguiText style={{
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: 14,
                        fontWeight: '600'
                      }}>
                        Actions: {actions.length}
                      </TamaguiText>
                    </YStack>
                  )}
                </YStack>
              </View>
            </YStack>
          </ScrollView>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={visible} onOpenChange={setVisible}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          style={{
            width: Platform.OS === 'web' ? '90%' : '95%',
            maxWidth: 800,
            maxHeight: '90%',
            backgroundColor: theme ? '#f8fafc' : '#0f172a',
            borderRadius: 16,
            padding: 0,
            overflow: 'hidden'
          }}
          enterStyle={{ scale: 0.9, opacity: 0 }}
          exitStyle={{ scale: 0.9, opacity: 0 }}
        >
          <YStack flex={1} gap={0}>
            {/* Header */}
            <XStack
              style={{
                paddingHorizontal: 24,
                paddingVertical: 20,
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}
            >
              <XStack gap={12} paddingHorizontal={24} alignItems="center">
                <View style={{
                  backgroundColor: theme ? '#ddd6fe' : '#4c1d95',
                  borderRadius: 12,
                  padding: 10
                }}>
                  <MaterialIcons
                    name="smart-toy"
                    size={24}
                    color={theme ? '#7c3aed' : '#a78bfa'}
                  />
                </View>
                <YStack>
                  <H2 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontSize: 24,
                    fontWeight: '700'
                  }}>
                    Create Agent
                  </H2>
                  <TamaguiText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14
                  }}>
                    Build an autonomous agent for your team
                  </TamaguiText>
                </YStack>
              </XStack>
              <Pressable style={{ left: -20 }} onPress={() => setVisible(false)}>
                <MaterialIcons
                  name="close"
                  size={24}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </Pressable>
            </XStack>

            {/* Stepper */}
            <CustomStepper activeStep={activeStep} steps={steps} />

            {/* Content */}
            <View style={{ flex: 1, minHeight: 0 }}>
              {renderStepContent(activeStep)}
            </View>

            {/* Footer */}
            <XStack
              style={{
                padding: 16,
                borderTopWidth: 1,
                borderTopColor: theme ? '#e2e8f0' : '#334155',
                alignItems: 'center',
                gap: 8
              }}
            >
              <TamaguiButton
                size="$4"
                variant="outlined"
                disabled={activeStep === 0}
                onPress={handleBack}
                style={{
                  borderColor: theme ? '#e2e8f0' : '#334155',
                  backgroundColor: 'transparent',
                  opacity: activeStep === 0 ? 0.5 : 1
                }}
              >
                <TamaguiText style={{
                  color: theme ? '#64748b' : '#94a3b8'
                }}>
                  Back
                </TamaguiText>
              </TamaguiButton>
              <XStack flex={1} />
              {activeStep < steps.length - 1 ? (
                <TamaguiButton
                  size="$4"
                  onPress={handleNext}
                  style={{
                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                  }}
                >
                  <TamaguiText style={{ color: 'white' }}>
                    Next
                  </TamaguiText>
                </TamaguiButton>
              ) : (
                <TamaguiButton
                  size="$4"
                  onPress={handleCreateAgent}
                  disabled={loading}
                  style={{
                    backgroundColor: theme ? '#10b981' : '#059669',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  <TamaguiText style={{ color: 'white' }}>
                    {loading ? 'Creating...' : 'Create Agent'}
                  </TamaguiText>
                </TamaguiButton>
              )}
            </XStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
});

const styles = StyleSheet.create({
  agentCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
  },
  checkboxItem: {
    borderRadius: 8,
    padding: 12,
    borderWidth: 2,
  },
  summaryCard: {
    borderRadius: 12,
    padding: 20,
  },
  radioOption: {
    borderRadius: 8,
    padding: 12,
    borderWidth: 2,
  }
});

AgentBuilderModal.displayName = 'AgentBuilderModal';

export default AgentBuilderModal;

